import React, { forwardRef, useRef, useState, cloneElement } from 'react';
import { useButton } from '@react-aria/button';
import FocusRing from '../util/FocusRing';
import mergeRefs from 'react-merge-refs';
import { useFocusable } from '@react-aria/focus';
import { filterComponentProps } from '../../utils/filterComponentProps';
import { cn } from '../../utils/cn';
import { useHover } from '@react-aria/interactions';
import { mergeProps } from '@react-aria/utils';
import { Text } from '../Text/Text';
const childStyles = cn('flex-shrink-0', 'flex flex-row justify-between items-center min-w-0 space-x-1');
const focusStyles = cn('focus:outline-none');
const textStyles = cn('whitespace-no-wrap select-none font-sans tracking-button truncate');
const inlineButtonStyles = ({ disabled, hovered, pressed, focused, }) => cn(focusStyles, textStyles, childStyles, 'p-2', 'border border-transparent', disabled ? 'text-concrete' : undefined, disabled ? 'cursor-not-allowed' : 'cursor-pointer', hovered && !disabled && 'border-l-bruise border-t-bruise border-r-bruise border-b-bruise', pressed ? 'bg-drywall' : 'bg-gesso');
export const SimpleInlineButton = forwardRef(function SimpleInlineButton(props, outerRef) {
    const innerRef = useRef(null);
    const ref = mergeRefs([outerRef, innerRef]);
    const { elementType: ElementType = 'button', isDisabled, children, icon } = props;
    const { hoverProps, isHovered } = useHover(props);
    const [isFocused, setIsFocused] = useState(false);
    const { focusableProps } = useFocusable({ ...props, onFocusChange: setIsFocused }, ref);
    return (React.createElement(ElementType, Object.assign({ ref: ref }, mergeProps(hoverProps, focusableProps, filterComponentProps(props), {
        className: inlineButtonStyles({
            disabled: isDisabled,
            pressed: false,
            focused: isFocused,
            hovered: isHovered,
        }),
    })),
        children && (React.createElement(Text, { as: "span", className: cn('flex-1', 'text-left truncate') }, children)),
        icon && cloneElement(icon, mergeProps(icon.props, { className: cn('flex-none') }))));
});
export const InlineButton = forwardRef(function InlineButton(props, outerRef) {
    const innerRef = useRef(null);
    const ref = mergeRefs([outerRef, innerRef]);
    const { elementType: ElementType = 'button', isDisabled, children, icon } = props;
    const { isPressed, buttonProps } = useButton(props, ref);
    const { hoverProps, isHovered } = useHover(props);
    const [isFocused, setIsFocused] = useState(false);
    const { focusableProps } = useFocusable({ ...props, onFocusChange: setIsFocused }, ref);
    return (React.createElement(FocusRing, { autoFocus: props.autoFocus },
        React.createElement(ElementType, Object.assign({ ref: ref }, mergeProps(buttonProps, hoverProps, focusableProps, filterComponentProps(props), {
            className: inlineButtonStyles({
                disabled: isDisabled,
                pressed: isPressed,
                focused: isFocused,
                hovered: isHovered,
            }),
        })),
            children && (React.createElement(Text, { as: "span", className: cn('flex-1', 'text-left truncate') }, children)),
            icon && cloneElement(icon, mergeProps(icon.props, { className: cn('flex-none') })))));
});
